import { api } from "@/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, PropsWithChildren, useContext } from "react";
import { getDepositoActual } from "../api/get-deposito-actual";
import { assignDeposito } from "../api/assign-deposito";
import { useAuthContext } from "@/features/auth/contexts/auth-context";
import { Permiso } from "@/shared/types/permiso";

const LOCAL_STORAGE_DEPOSITO_KEY = "deposito";
const getStorageKey = (organizacionId?: string) => `${LOCAL_STORAGE_DEPOSITO_KEY}:${organizacionId}`;

type DepositoData = {
    id: string,
    nombre: string,
    principal: boolean
}

type LocalData = DepositoData & {
    token: string
}

export interface DepositoContextReturn {
    assign: (depositoId: string) => Promise<DepositoData>,
    revoke: () => Promise<void>,
    reload: () => Promise<void>,
    hasDepositoAssigned: boolean,
    depositoData: DepositoData,
    loading: boolean,
    error: Error | undefined
}

const DepositoContext = createContext<DepositoContextReturn>({
    assign: async () => null!,
    revoke: async () => { },
    reload: async () => { },
    hasDepositoAssigned: false,
    depositoData: null!,
    error: undefined,
    loading: false,
});

export function useDepositoContext() {
    return useContext(DepositoContext);
}

export function DepositoContextProvider({
    children
}: PropsWithChildren) {
    const { isLoggedIn, hasPermisos, authData } = useAuthContext();
    const organizacionId = authData?.organizacionId;

    // RootAdmin no necesita depósito asignado
    const isRootAdmin = hasPermisos(Permiso.ManageOrganizaciones);

    const queryClient = useQueryClient();
    const { data: depositoData, isLoading, error, refetch } = useQuery({
        queryFn: async () => {
            try {
                const localData = getLocalData();
                if (!localData) {
                    delete api.defaults.headers.common["X-Deposito-Token"];
                    return null;
                }

                const response = await getDepositoActual(localData.token);
                setLocalData(response);
                return response;
            } catch (e) {
                deleteLocalData();
                return null;
            }
        },
        queryKey: ["deposito", organizacionId],
        enabled: isLoggedIn && !isRootAdmin
    })

    const assign = async (depositoId: string) => {
        const depositoData = await assignDeposito(depositoId);
        setLocalData(depositoData);
        queryClient.setQueryData(["deposito", organizacionId], depositoData);
        return depositoData;
    }

    const revoke = async () => {
        deleteLocalData();
        queryClient.setQueryData(["deposito", organizacionId], null);
    }

    const reload = async () => {
        await refetch();
    }

    //Private
    const getLocalData = () => {
        if (!organizacionId) return undefined;
        const stringData = localStorage.getItem(getStorageKey(organizacionId));
        if (!stringData) return undefined;
        return JSON.parse(stringData) as LocalData;
    }

    const setLocalData = (data: LocalData) => {
        localStorage.setItem(getStorageKey(organizacionId), JSON.stringify(data));
        api.defaults.headers.common["X-Deposito-Token"] = `${data.token}`
    }

    const deleteLocalData = () => {
        localStorage.removeItem(getStorageKey(organizacionId));
        delete api.defaults.headers.common["X-Deposito-Token"];
    }

    // Para RootAdmin, devolver valores por defecto que no requieren depósito
    if (isRootAdmin) {
        return <DepositoContext.Provider value={{
            assign: async () => null!,
            revoke: async () => { },
            reload: async () => { },
            hasDepositoAssigned: true,
            depositoData: null!,
            error: undefined,
            loading: false,
        }}>
            {children}
        </DepositoContext.Provider>
    }

    return <DepositoContext.Provider value={{
        assign: assign,
        revoke: revoke,
        reload: reload,
        hasDepositoAssigned: Boolean(depositoData),
        depositoData: depositoData!,
        error: error || undefined,
        loading: isLoading,
    }}>
        {children}
    </DepositoContext.Provider>
}
